import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Users, UserCheck, UserX, Clock, CheckCircle, XCircle, 
  FileText, Upload, Mail, Phone, MapPin, Building2, Eye, Download,
  Plus, Search, Filter, ArrowLeft, Edit, Save, Trash2 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnhancedClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [groupedClients, setGroupedClients] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // New client form
  const [newClient, setNewClient] = useState({
    client_type: 'professional',
    name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    siret: '',
    siren: '',
    tva_number: '',
    company_form: '',
    contract_signatory_name: '',
    first_name: '',
    last_name: '',
    birth_date: '',
    administration_name: '',
    department: '',
    service: '',
    notes: ''
  });

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const handleBack = () => {
    window.location.href = '/dashboard';
  };

  useEffect(() => {
    loadClients();
  }, [filterType, filterStatus, searchQuery, selectedLetter]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const params = new URLSearchParams();
      
      if (filterType !== 'all') params.append('client_type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (searchQuery) params.append('search', searchQuery);
      if (selectedLetter) params.append('letter', selectedLetter);
      
      const response = await axios.get(`${API}/admin/clients/enhanced?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setClients(response.data.clients || []);
      setGroupedClients(response.data.grouped_by_letter || {});
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.post(`${API}/admin/clients/create`, newClient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Client créé avec succès');
      setShowCreateModal(false);
      resetNewClientForm();
      loadClients();
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error('Erreur lors de la création du client');
    } finally {
      setLoading(false);
    }
  };

  const resetNewClientForm = () => {
    setNewClient({
      client_type: 'professional',
      name: '',
      email: '',
      phone: '',
      address: '',
      postal_code: '',
      city: '',
      siret: '',
      siren: '',
      tva_number: '',
      company_form: '',
      contract_signatory_name: '',
      first_name: '',
      last_name: '',
      birth_date: '',
      administration_name: '',
      department: '',
      service: '',
      notes: ''
    });
  };

  const handleViewClient = async (clientId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/admin/clients/enhanced/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedClient(response.data);
      setShowClientModal(true);
    } catch (error) {
      console.error('Error loading client details:', error);
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateClient = async (clientId) => {
    try {
      const token = localStorage.getItem('tkale_token');
      await axios.post(`${API}/admin/clients/enhanced/${clientId}/validate-legal`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Client validé avec succès');
      loadClients();
      if (selectedClient) {
        handleViewClient(clientId);
      }
    } catch (error) {
      console.error('Error validating client:', error);
      toast.error('Erreur lors de la validation');
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.get(`${API}/admin/clients/enhanced/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_tkale_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export Excel généré avec succès');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', label: 'En attente' },
      'approved': { color: 'bg-green-100 text-green-800', label: 'Approuvé' },
      'active': { color: 'bg-blue-100 text-blue-800', label: 'Actif' },
      'suspended': { color: 'bg-red-100 text-red-800', label: 'Suspendu' },
      'rejected': { color: 'bg-gray-100 text-gray-800', label: 'Rejeté' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const getClientTypeLabel = (type) => {
    const types = {
      'professional': 'Professionnel',
      'individual': 'Particulier',
      'administration': 'Administration'
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button onClick={handleBack} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-slate-800">Gestion des Clients</h1>
          </div>
          <div className="flex space-x-2">
            <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Client
            </Button>
            <Button onClick={handleExportExcel} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="max-w-7xl mx-auto mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <Label>Rechercher</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input 
                  placeholder="Nom de l'entreprise ou du client..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Type Filter */}
            <div>
              <Label>Type de client</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="professional">Professionnel</SelectItem>
                  <SelectItem value="individual">Particulier</SelectItem>
                  <SelectItem value="administration">Administration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div>
              <Label>Statut</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="approved">Approuvé</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="suspended">Suspendu</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alphabet Navigation */}
      <Card className="max-w-7xl mx-auto mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            <Button 
              size="sm" 
              variant={selectedLetter === '' ? 'default' : 'outline'}
              onClick={() => setSelectedLetter('')}
            >
              Tous
            </Button>
            {alphabet.map(letter => {
              const count = groupedClients[letter]?.length || 0;
              return (
                <Button 
                  key={letter}
                  size="sm" 
                  variant={selectedLetter === letter ? 'default' : 'outline'}
                  onClick={() => setSelectedLetter(letter)}
                  disabled={count === 0}
                  className="w-10"
                >
                  {letter}
                  {count > 0 && <span className="ml-1 text-xs">({count})</span>}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Clients List */}
      <Card className="max-w-7xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Liste des Clients ({clients.length})</span>
            {loading && <span className="text-sm text-gray-500">Chargement...</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Chargement...</div>
          ) : clients.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Aucun client trouvé
            </div>
          ) : (
            <div className="space-y-3">
              {clients.map((client) => (
                <div key={client.client_id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold">{client.name}</h3>
                        {getStatusBadge(client.status)}
                        <Badge variant="outline">{getClientTypeLabel(client.client_type)}</Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-2" />
                          {client.email}
                        </div>
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-2" />
                          {client.phone}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {client.city}
                        </div>
                        {client.siret && (
                          <div className="flex items-center">
                            <Building2 className="w-4 h-4 mr-2" />
                            SIRET: {client.siret}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 mt-2 text-xs">
                        <span className={client.documents_complete ? 'text-green-600' : 'text-orange-600'}>
                          {client.documents_complete ? '✓ Documents complets' : '⚠ Documents manquants'}
                        </span>
                        <span className={client.legal_validation_complete ? 'text-green-600' : 'text-gray-500'}>
                          {client.legal_validation_complete ? '✓ Validation légale' : '○ Validation en attente'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleViewClient(client.client_id)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Voir
                      </Button>
                      {!client.legal_validation_complete && client.documents_complete && (
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleValidateClient(client.client_id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Valider
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Client Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer un Nouveau Client</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateClient} className="space-y-4">
            {/* Client Type */}
            <div>
              <Label>Type de client *</Label>
              <Select 
                value={newClient.client_type} 
                onValueChange={(value) => setNewClient({...newClient, client_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professionnel</SelectItem>
                  <SelectItem value="individual">Particulier</SelectItem>
                  <SelectItem value="administration">Administration</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Common Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom / Raison Sociale *</Label>
                <Input 
                  value={newClient.name}
                  onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Téléphone *</Label>
                <Input 
                  value={newClient.phone}
                  onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Code Postal *</Label>
                <Input 
                  value={newClient.postal_code}
                  onChange={(e) => setNewClient({...newClient, postal_code: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Ville *</Label>
                <Input 
                  value={newClient.city}
                  onChange={(e) => setNewClient({...newClient, city: e.target.value})}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Adresse complète *</Label>
              <Input 
                value={newClient.address}
                onChange={(e) => setNewClient({...newClient, address: e.target.value})}
                required
              />
            </div>

            {/* Professional Fields */}
            {newClient.client_type === 'professional' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>SIRET</Label>
                    <Input 
                      value={newClient.siret}
                      onChange={(e) => setNewClient({...newClient, siret: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>SIREN</Label>
                    <Input 
                      value={newClient.siren}
                      onChange={(e) => setNewClient({...newClient, siren: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>N° TVA Intracommunautaire</Label>
                    <Input 
                      value={newClient.tva_number}
                      onChange={(e) => setNewClient({...newClient, tva_number: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Forme juridique (SARL, SAS, etc.)</Label>
                    <Input 
                      value={newClient.company_form}
                      onChange={(e) => setNewClient({...newClient, company_form: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Nom du signataire du contrat</Label>
                  <Input 
                    value={newClient.contract_signatory_name}
                    onChange={(e) => setNewClient({...newClient, contract_signatory_name: e.target.value})}
                  />
                </div>
              </>
            )}

            {/* Individual Fields */}
            {newClient.client_type === 'individual' && (
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Prénom</Label>
                  <Input 
                    value={newClient.first_name}
                    onChange={(e) => setNewClient({...newClient, first_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input 
                    value={newClient.last_name}
                    onChange={(e) => setNewClient({...newClient, last_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Date de naissance</Label>
                  <Input 
                    type="date"
                    value={newClient.birth_date}
                    onChange={(e) => setNewClient({...newClient, birth_date: e.target.value})}
                  />
                </div>
              </div>
            )}

            {/* Administration Fields */}
            {newClient.client_type === 'administration' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom de l'administration</Label>
                  <Input 
                    value={newClient.administration_name}
                    onChange={(e) => setNewClient({...newClient, administration_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Département</Label>
                  <Input 
                    value={newClient.department}
                    onChange={(e) => setNewClient({...newClient, department: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Service</Label>
                  <Input 
                    value={newClient.service}
                    onChange={(e) => setNewClient({...newClient, service: e.target.value})}
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={newClient.notes}
                onChange={(e) => setNewClient({...newClient, notes: e.target.value})}
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Création...' : 'Créer le Client'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Details Modal */}
      {selectedClient && (
        <Dialog open={showClientModal} onOpenChange={setShowClientModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Détails du Client</span>
                {getStatusBadge(selectedClient.client.status)}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Client Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informations Client</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-600">Type</Label>
                      <p>{getClientTypeLabel(selectedClient.client.client_type)}</p>
                    </div>
                    <div>
                      <Label className="text-gray-600">Nom</Label>
                      <p className="font-semibold">{selectedClient.client.name}</p>
                    </div>
                    <div>
                      <Label className="text-gray-600">Email</Label>
                      <p>{selectedClient.client.email}</p>
                    </div>
                    <div>
                      <Label className="text-gray-600">Téléphone</Label>
                      <p>{selectedClient.client.phone}</p>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-gray-600">Adresse</Label>
                      <p>{selectedClient.client.address}, {selectedClient.client.postal_code} {selectedClient.client.city}</p>
                    </div>
                    {selectedClient.client.siret && (
                      <>
                        <div>
                          <Label className="text-gray-600">SIRET</Label>
                          <p>{selectedClient.client.siret}</p>
                        </div>
                        <div>
                          <Label className="text-gray-600">SIREN</Label>
                          <p>{selectedClient.client.siren}</p>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Documents */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>Documents ({selectedClient.documents?.length || 0})</span>
                    <Button size="sm">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedClient.documents && selectedClient.documents.length > 0 ? (
                    <div className="space-y-2">
                      {selectedClient.documents.map((doc) => (
                        <div key={doc.document_id} className="flex items-center justify-between p-3 border rounded">
                          <div className="flex items-center space-x-3">
                            <FileText className="w-5 h-5 text-blue-600" />
                            <div>
                              <p className="font-medium">{doc.file_name}</p>
                              <p className="text-xs text-gray-500">{doc.document_type} • {(doc.file_size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {doc.is_validated ? (
                              <Badge className="bg-green-100 text-green-800">Validé</Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>
                            )}
                            <Button size="sm" variant="outline">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-4">Aucun document</p>
                  )}
                  
                  {selectedClient.client.missing_documents && selectedClient.client.missing_documents.length > 0 && (
                    <Alert className="mt-4">
                      <AlertDescription>
                        <strong>Documents manquants:</strong> {selectedClient.client.missing_documents.join(', ')}
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex justify-end space-x-2">
                {!selectedClient.client.legal_validation_complete && selectedClient.client.documents_complete && (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      handleValidateClient(selectedClient.client.client_id);
                      setShowClientModal(false);
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Valider Légalement
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowClientModal(false)}>
                  Fermer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EnhancedClientManagement;